import * as React from 'react';
import { Avatar, Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import { Send, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { QueryClient, useMutation } from '@tanstack/react-query';
import { getComments, createComment } from '../../controllers/posts/comments';
import { DeletePost } from '../post/card/PostActions';
import { UserContext } from '../../context/UserContext';

const NAV = '#202c41';
const MUTED = '#8A95A3';
const ACCENT = '#732982';
const BG = '#F6F3F8';

const getTimeAgo = (dateString) => {
  const diffInMinutes = Math.floor((new Date() - new Date(dateString)) / (1000 * 60));
  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
  return new Date(dateString).toLocaleDateString();
};

const cleanContent = (html) => (html ?? '')
  .replaceAll(/(<([^>]+)>)/ig, '')
  .replaceAll('&#39;', "'")
  .replace(/^(@\S+\s*)+/, '')
  .trim();

export default function EventMixer({ id, token, canPost }) {
  const { userDetails } = React.useContext(UserContext);
  const navigate = useNavigate();
  const [comments, setComments] = React.useState(null);
  const [content, setContent] = React.useState('');
  const [replyTo, setReplyTo] = React.useState(null);

  const getAPIComments = async () => {
    setComments(await getComments(id, token));
  };

  React.useEffect(() => {
    getAPIComments();
    const interval = setInterval(getAPIComments, 8000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, token]);

  // Flat, chronological chat rather than nested forum threading — this is a group
  // chat for coordinating the event, not a discussion board. Newest first.
  const messages = React.useMemo(() => {
    const flat = (comments?.descendants ?? []).slice();
    flat.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    return flat.reverse();
  }, [comments?.descendants]);

  const messagesById = React.useMemo(() => {
    const map = {};
    (comments?.descendants ?? []).forEach((c) => { map[c.id] = c; });
    return map;
  }, [comments?.descendants]);

  const queryClient = new QueryClient();
  const mutation = useMutation({
    mutationFn: createComment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['createComment'] });
      setContent('');
      setReplyTo(null);
      getAPIComments();
    },
  });

  const send = () => {
    if (mutation.isPending || !content.trim()) return;
    mutation.mutate({
      body: {
        status: replyTo?.account?.acct ? `@${replyTo.account.acct} ${content}` : content,
        in_reply_to_id: replyTo?.id ?? id,
        visibility: 'unlisted',
      },
      token,
    });
  };

  const canSend = content.trim().length > 0 && !mutation.isPending;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column',
      height: '65vh', minHeight: 420,
      borderRadius: 3, overflow: 'hidden',
      border: '1px solid #ECE6F0',
      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    }}>
      {/* ── Header (fixed) ── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 2, background: BG, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 26, mt: 0.25 }}>🥂</Typography>
        <Box>
          <Typography sx={{ fontSize: 17, fontWeight: 900, color: NAV, mb: 0.25 }}>The Pre-Mixer</Typography>
          <Typography sx={{ fontSize: 12.5, color: MUTED, lineHeight: 1.4 }}>
            Chat with the host and other attendees — make plans, coordinate rides, and meet new friends before you even get there.
          </Typography>
        </Box>
      </Box>

      {/* ── Compose bar (fixed) ── */}
      {canPost ? (
        <Box sx={{ px: 2, py: 1.5, background: '#fff', borderBottom: '1px solid #ECE6F0', flexShrink: 0 }}>
          {replyTo && (
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              background: '#F3EAF6', borderRadius: 1.5, px: 1.25, py: 0.5, mb: 1,
            }}>
              <Typography sx={{ color: ACCENT, fontSize: 12.5, fontWeight: 700 }} noWrap>
                Replying to @{replyTo.account?.acct}
              </Typography>
              <IconButton size="small" onClick={() => setReplyTo(null)} sx={{ color: ACCENT }}>
                <Close sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <TextField
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
              placeholder="Say hey to the group…"
              multiline
              maxRows={4}
              fullWidth
              size="small"
              disabled={mutation.isPending}
              sx={{
                background: '#F0F2F5',
                '& .MuiOutlinedInput-root': { borderRadius: 5, '& fieldset': { border: 'none' } },
              }}
            />
            <IconButton
              onClick={send}
              disabled={!canSend}
              sx={{
                background: canSend ? ACCENT : '#E4E0E8',
                color: canSend ? '#fff' : MUTED,
                width: 40, height: 40, flexShrink: 0,
                '&:hover': { background: canSend ? '#5c1f68' : '#E4E0E8' },
              }}
            >
              {mutation.isPending ? <CircularProgress size={18} sx={{ color: MUTED }} /> : <Send sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
          {mutation.isError && (
            <Typography sx={{ color: 'error.main', fontSize: 12, mt: 0.5 }}>
              Couldn't send that. Try again.
            </Typography>
          )}
        </Box>
      ) : (
        <Box sx={{ px: 2, py: 1.5, background: '#fff', borderBottom: '1px solid #ECE6F0', flexShrink: 0, textAlign: 'center' }}>
          <Typography sx={{ color: MUTED, fontSize: 13, fontWeight: 600 }}>
            RSVP to this event to join The Pre-Mixer.
          </Typography>
        </Box>
      )}

      {/* ── Messages (newest first, own scroll region) ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, background: '#fff' }}>
        {comments === null ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress size={24} sx={{ color: ACCENT }} />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ textAlign: 'center', pt: 5 }}>
            <Typography sx={{ fontSize: 34, mb: 1 }}>👋</Typography>
            <Typography sx={{ fontWeight: 800, color: NAV, fontSize: 16 }}>No one's said hi yet</Typography>
            <Typography sx={{ color: MUTED, fontSize: 13 }}>Be the first to break the ice.</Typography>
          </Box>
        ) : (
          messages.map((m, i) => {
            const isMe = m?.account?.id === userDetails?.id;
            const parent = m.in_reply_to_id && m.in_reply_to_id !== id ? messagesById[m.in_reply_to_id] : null;
            return (
              <Box key={m.id ?? i} sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', mb: 2 }}>
                {!isMe && (
                  <Avatar
                    src={m.account.avatar}
                    onClick={() => navigate('/profile/' + m.account.id)}
                    sx={{ width: 34, height: 34, mr: 1, cursor: 'pointer' }}
                  />
                )}
                <Box sx={{ maxWidth: '72%' }}>
                  {!isMe && (
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, mb: 0.4, ml: 0.25 }}>
                      {m.account.display_name || m.account.username}
                    </Typography>
                  )}
                  {parent && (
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4, ml: 0.25,
                      justifyContent: isMe ? 'flex-end' : 'flex-start',
                    }}>
                      <Typography sx={{ fontSize: 11, color: MUTED }}>↩</Typography>
                      <Typography sx={{ fontSize: 11, color: MUTED, fontStyle: 'italic' }} noWrap>
                        Replying to {parent.account?.id === userDetails?.id ? 'you' : `@${parent.account?.acct}`}: “{cleanContent(parent.content).slice(0, 40)}”
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{
                    px: 1.75, py: 1.1, borderRadius: 3,
                    background: isMe ? ACCENT : '#fff',
                    color: isMe ? '#fff' : '#333',
                    borderBottomRightRadius: isMe ? 4 : undefined,
                    borderBottomLeftRadius: isMe ? undefined : 4,
                    boxShadow: isMe ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
                    border: isMe ? 'none' : '1px solid #F0F0F5',
                  }}>
                    <Typography sx={{ fontSize: 14.5, lineHeight: 1.4 }}>{cleanContent(m.content)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, ml: 0.25, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                    <Typography sx={{ fontSize: 11, color: MUTED }}>{getTimeAgo(m.created_at)}</Typography>
                    {canPost && (
                      <Typography
                        onClick={() => setReplyTo(m)}
                        sx={{ fontSize: 11, color: ACCENT, fontWeight: 700, cursor: 'pointer' }}
                      >
                        Reply
                      </Typography>
                    )}
                    {isMe && (
                      <DeletePost id={m.id} token={token} dontRedirect={getAPIComments} />
                    )}
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
}
